import { useEffect, useState, useCallback, useRef } from 'react'
import { api, auth, isConversationLost } from '../lib/api.js'
import { outcomeLabel } from '../lib/outcomes.js'
import { displayOutcome, paymentLabel } from '../lib/orderPayment.js'
import { OrderPaymentStatus } from '../components/OrderSync.jsx'
import DateRangePicker from '../components/DateRangePicker.jsx'
import { MediaBubble, KbMediaBubble, findSentMedia, ImageLightbox } from '../components/Media.jsx'
import { formatBeijingTime } from '../lib/time.js'

const fmtTime = t => formatBeijingTime(t, {
  year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
})
const fmtDay = t => formatBeijingTime(t, { month: '2-digit', day: '2-digit' })

// 工作台只看得到每个客户最新那一通（列表按身份去重），这里是唯一能翻出全部历史的地方。
export default function Records() {
  const me = auth.agent
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [agentId, setAgentId] = useState(me?.id || '')   // 默认看自己的，要看别人就切
  const [search, setSearch] = useState('')
  const [agents, setAgents] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const [cust, setCust] = useState(null)      // 选中的客户
  const [convs, setConvs] = useState([])      // 该客户的全部会话
  const [convId, setConvId] = useState(null)  // 选中的那一通
  const [msgs, setMsgs] = useState([])
  const [kw, setKw] = useState('')            // 会话内容关键词
  const [lightbox, setLightbox] = useState('')
  const [media, setMedia] = useState([])      // 素材库，用来把「已发送图片」回执还原成图

  useEffect(() => { api.listAgents().then(setAgents).catch(() => {}) }, [])
  useEffect(() => { api.listMedia().then(setMedia).catch(() => {}) }, [])

  const load = useCallback(async () => {
    setLoading(true); setErr('')
    try {
      setRows(await api.listCustomerHistory({
        from: from || undefined, to: to || undefined,
        agentId: agentId || undefined, search: search.trim() || undefined,
      }))
    } catch (e) { setErr(e.message) } finally { setLoading(false) }
  }, [from, to, agentId, search])

  useEffect(() => {
    const t = setTimeout(load, 300)   // 搜索框防抖
    return () => clearTimeout(t)
  }, [load])

  // 每次点开客户/会话都记一个序号，响应回来时序号对不上就是被后来的点击取代了，直接丢弃。
  // 否则先点 A 再点 B，A 的慢响应后到会把 A 的聊天显示在 B 的客户名下
  const custSeq = useRef(0)
  const convSeq = useRef(0)

  async function openCustomer(r) {
    const seq = ++custSeq.current
    convSeq.current++
    setCust(r); setConvs([]); setConvId(null); setMsgs([]); setKw('')
    try {
      const list = await api.listCustomerConversations(r.user_ids, r.order_id)
      if (seq !== custSeq.current) return
      setConvs(list)
      if (list[0]) openConv(list[0].id)
    } catch (e) { if (seq === custSeq.current) setErr(e.message) }
  }

  async function openConv(id) {
    const seq = ++convSeq.current
    setConvId(id); setMsgs([])
    try {
      const list = await api.listMessages(id, 500)
      if (seq === convSeq.current) setMsgs(list)
    } catch (e) {
      if (seq !== convSeq.current) return
      // 会话被转出/归属被重新核实/已删除：说清楚原因，不要显示成"服务器故障"
      setErr(isConversationLost(e) ? '这通会话已转出或权限已变更，看不了' : e.message)
    }
  }

  const agentName = id => agents.find(a => a.id === id)?.name || id || '—'
  const scopeLabel = agentId ? agentName(agentId) : '全部运营'
  const rangeLabel = from && to ? `${from} 至 ${to}` : '全部时间'

  // 关键词只过滤文字消息；媒体消息没有可搜的文本，搜索时先让位给文字结果
  const shown = kw.trim()
    ? msgs.filter(m => (m.content || '').toLowerCase().includes(kw.trim().toLowerCase()))
    : msgs

  return (
    <div className="records-page" style={{ padding: 20 }}>
      <div className="card records-filters" style={{ padding: 16, marginBottom: 16 }}>
        <div className="row records-filter-controls" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker from={from} to={to} onChange={(a, b) => { setFrom(a); setTo(b) }} />
          <select className="input" value={agentId} onChange={e => setAgentId(e.target.value)}
            style={{ width: 150 }}>
            <option value="">全部运营</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
          </select>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="车牌 / 手机号" style={{ width: 180 }} />
          {(from || to || search || agentId) && (
            <button className="btn btn-sm" onClick={() => {
              setFrom(''); setTo(''); setSearch(''); setAgentId('')
            }}>清除筛选</button>
          )}
        </div>
        <div className="small muted" style={{ marginTop: 10 }}>
          {loading ? '查询中…' : <>
            <b style={{ color: 'var(--text)' }}>{scopeLabel}</b> 在 <b style={{ color: 'var(--text)' }}>{rangeLabel}</b> 接待了
            {' '}<b style={{ color: 'var(--brand)', fontSize: 15 }}>{rows.length}</b> 个客户
            <span style={{ marginLeft: 10 }}>
              （同一车牌/手机号不论进线多少次都算一个客户；按客户当前归属统计）
            </span>
          </>}
        </div>
      </div>

      {err && <div style={{ padding: '8px 12px', background: '#fef2f2', color: 'var(--danger)', fontSize: 13, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      <div className="records-columns" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* 客户列表 */}
        <div className="card records-customers" style={{ width: 300, flexShrink: 0, maxHeight: 'calc(100vh - 170px)', overflow: 'auto' }}>
          {!rows.length && !loading && (
            <div className="muted small" style={{ padding: 20, textAlign: 'center' }}>没有符合条件的记录</div>
          )}
          {rows.map(r => (
            <div className="records-customer" key={r.cust_key} onClick={() => openCustomer(r)}
              style={{
                padding: '11px 14px', borderBottom: '1px solid var(--line)', cursor: 'pointer',
                background: cust?.cust_key === r.cust_key ? 'var(--select)' : 'transparent',
              }}>
              <div className="spread">
                <b>{r.nickname || r.cust_key.replace(/^wecom:/, '')}</b>
                <span className="small muted">{r.conv_count} 次进线</span>
              </div>
              <div className="small muted" style={{ marginTop: 3 }}>
                归属 {agentName(r.owner_agent_id)}
                {' · '}{fmtDay(r.first_at)} ~ {fmtDay(r.last_at)}
                {displayOutcome(r) ? <span style={{ color: 'var(--brand)' }}>{' · '}{outcomeLabel(displayOutcome(r))}</span> : ''}
                {r.order_payment && <span>{' · '}{paymentLabel(r)}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* 选中客户的会话与聊天内容 */}
        <div className="card records-detail" style={{ flex: 1, minWidth: 0, height: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column' }}>
          {!cust ? (
            <div className="muted small" style={{ padding: 40, textAlign: 'center' }}>
              左边点一个客户，这里显示他全部的进线记录和聊天内容
            </div>
          ) : (
            <>
              <div className="records-detail-header" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
                <div className="spread" style={{ marginBottom: 8 }}>
                  <b>{cust.nickname || cust.cust_key.replace(/^wecom:/, '')}</b>
                  <span className="small muted">共 {convs.length} 次进线（不受上方时间筛选限制）</span>
                </div>
                <div className="row records-sessions" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {convs.map((c, i) => (
                    <button key={c.id} className="btn btn-sm"
                      onClick={() => openConv(c.id)}
                      style={convId === c.id
                        ? { borderColor: 'var(--brand)', color: 'var(--brand)' } : undefined}>
                      第{convs.length - i}次 · {fmtDay(c.created_at)} · {c.msg_count}条
                    </button>
                  ))}
                </div>
                <OrderPaymentStatus conv={convs.find(c => c.id === convId) || cust} />
              </div>

              <div className="records-search" style={{ padding: '8px 16px', borderBottom: '1px solid var(--line)' }}>
                <input className="input" value={kw} onChange={e => setKw(e.target.value)}
                  placeholder="在这通会话里搜关键词，方便找内容" style={{ width: '100%' }} />
                {kw.trim() && (
                  <div className="small muted" style={{ marginTop: 5 }}>
                    命中 {shown.length} 条
                    <button className="btn-link small" style={{ marginLeft: 8 }} onClick={() => setKw('')}>显示全部</button>
                  </div>
                )}
              </div>

              <div className="records-messages" style={{ flex: 1, overflow: 'auto', padding: '16px 24px', background: 'var(--chat-bg)' }}>
                {!shown.length && (
                  <div className="muted small" style={{ textAlign: 'center', padding: 20 }}>
                    {kw.trim() ? '这通会话里没有包含该关键词的消息' : '这通会话没有消息'}
                  </div>
                )}
                {shown.map(m => {
                  if (m.role === 'system') {
                    return <div key={m.id} className="muted small" style={{ textAlign: 'center', margin: '10px 0' }}>{m.content}</div>
                  }
                  const mine = m.role === 'assistant' || m.role === 'agent'
                  const sentMed = findSentMedia(m, media)
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 16 }}>
                      <div className="records-message" style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                        <div className="muted small" style={{ marginBottom: 4, padding: '0 10px', fontSize: 12 }}>
                          {m.role === 'user' ? '客户'
                            : m.role === 'agent' ? agentName(m.agent_id)
                            : 'AI小助'}
                          {' · '}{fmtTime(m.created_at)}
                          {m.meta?.recalled_at && <span style={{ color: 'var(--danger)' }}> · 已撤回</span>}
                        </div>
                        <div className={`bubble ${m.role === 'user' ? 'them' : m.role === 'agent' ? 'agent' : 'ai'}`}
                          style={m.meta?.recalled_at ? { opacity: .45 } : undefined}>
                          {m.meta?.media
                            ? <><MediaBubble media={m.meta.media} delivery={m.meta.delivery} onViewImage={setLightbox} />{(m.meta.asr || m.meta.vision) && <div className="small" style={{ marginTop: 6 }} title="语音自动识别的文字">{m.meta.vision ? `识图：${m.meta.vision}` : m.content}</div>}</>
                            : sentMed ? <KbMediaBubble med={sentMed} onViewImage={setLightbox} />
                            : m.content}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {lightbox && <ImageLightbox url={lightbox} onClose={() => setLightbox('')} />}
    </div>
  )
}
